import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext.js';

import DisplayCardSubmitSettings  from './DisplayCardSubmitSettings';
import DisplayTableSubmitSettings from './DisplayTableSubmitSettings';

/* -------------------------------------------------------------------------
 * Human-readable labels for the action keys
 * ---------------------------------------------------------------------- */
const actionLabels = {
  redirect:               'Redirect',
  sendEmail:              'Send Email',
  displayToast:           'Display Toast',
  displayInCard:          'Display in Card',
  addToSubmissionsTable:  'Display Table',
};

/* =========================================================================
 *  FormSubmitOptionsSettings
 * -------------------------------------------------------------------------
 *  • Receives `selectedAction`   → which submission action is being edited
 *  • Receives `onSave`           → parent callback invoked only on “Done”
 *  • Receives `onNavigateBack`   → closes the side-panel
 * ========================================================================= */
const FormSubmitOptionsSettings = ({ selectedAction, onSave, onNavigateBack }) => {
  /* ---------------------------------------------------------------------
   * Pull current settings from context; keep local copy for edits
   * ------------------------------------------------------------------ */
  const { submissionSettings, updateSettings } = useFormSubmissionSettings();
  const [localSettings, setLocalSettings]     = useState({});

  /* Sync local copy whenever the selected action changes or
     the context updates externally (e.g. from another tab) */
  useEffect(() => {
    if (selectedAction) {
      setLocalSettings(submissionSettings[selectedAction] || {});
    }
  }, [selectedAction, submissionSettings]);

  /* -----------------------------------------------------------
   * Persist local changes back to parent ONLY when the user
   * clicks “Done” in the TopBarFieldSettings component
   * -------------------------------------------------------- */
  const handleSave = useCallback(() => {
    onSave(localSettings);
  }, [localSettings, onSave]);

  /* “Done” button workflow:
     1. push localSettings → global context
     2. notify parent via onSave
     3. close side-panel via onNavigateBack                       */
  const handleDoneClick = useCallback(() => {
    updateSettings(selectedAction, localSettings);
    handleSave();
    onNavigateBack();
  }, [selectedAction, localSettings, updateSettings, handleSave, onNavigateBack]);

  /* ------------------------------------------------------------------ *
   *  Render
   * ------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        pt: 0,
        px: 0,
        pb: 0,
        width:      '100%',
        maxWidth:   '100%',
        height:     '100%',
        overflowY:  'auto',
        position:   'relative',
      }}
    >
      {/* ----------------------------------------------------------------
       * Section header (“Edit: Display in Card” etc.)
       * ------------------------------------------------------------- */}
      <Typography
        variant="subtitle1"
        sx={{
          mt:            0,
          ml:            0,
          mb:            0.5,
          fontWeight:    'bold',
          fontSize:      '1.2rem',
        }}
      >
        Edit:&nbsp;{actionLabels[selectedAction] || selectedAction}
      </Typography>

      {/* ---------------------------------------------------------------
       * Per-action settings UIs
       * ------------------------------------------------------------ */}
      {selectedAction === 'displayInCard' && (
        <DisplayCardSubmitSettings
          /* Pass a *nested* sub-object so the child only mutates its own area */
          settings={localSettings.displayInCard || {}}
          onChange={(updated) =>
            setLocalSettings((prev) => ({
              ...prev,
              displayInCard: updated,
            }))
          }
          onSave={handleSave}
        />
      )}

      {selectedAction === 'addToSubmissionsTable' && (
        <DisplayTableSubmitSettings
          settings={localSettings.addToSubmissionsTable || {}}
          onChange={(updated) =>
            setLocalSettings((prev) => ({
              ...prev,
              addToSubmissionsTable: updated,
            }))
          }
          onSave={handleSave}
        />
      )}

      {/* Add additional branches for other actions here (redirect, sendEmail …) */}
    </Box>
  );
};

export default FormSubmitOptionsSettings;
